import React, { memo, useRef, KeyboardEvent, ClipboardEvent, ChangeEvent } from "react";
import XGSIcon from "ui-components/icon/xgsIcon";
import XGSIcons from "ui-components/icon/xgsIcons";
import {
  ProbillEntry,
  createEntryId,
  formatProbillDisplay,
  parseProbillToken,
  MAX_VALID_PROBILLS,
} from "./types";

export interface ProbillChipInputProps {
  entries: ProbillEntry[];
  onEntriesChange: (entries: ProbillEntry[]) => void;
  inputValue: string;
  onInputValueChange: (value: string) => void;
  /** Caps format-valid chips. Print & Download keeps the default of 100. */
  maxValid?: number;
  emptyPlaceholder?: string;
  addPlaceholder?: string;
  invalidFormatMessage?: string;
  capNoun?: string;
}

const ProbillChipInput: React.FC<ProbillChipInputProps> = memo((props) => {
  const {
    entries,
    onEntriesChange,
    inputValue,
    onInputValueChange,
    maxValid = MAX_VALID_PROBILLS,
    emptyPlaceholder = "Type or paste probill numbers…",
    addPlaceholder = "Add another…",
    invalidFormatMessage = "Invalid probill format",
    capNoun = "invoices",
  } = props;
  const inputRef = useRef<HTMLInputElement>(null);

  const validCount = entries.filter((e) => e.status === "valid").length;
  const invalidCount = entries.filter((e) => e.status === "invalid").length;
  const validatingCount = entries.filter((e) => e.status === "validating").length;

  const countedTowardCap = (list: ProbillEntry[]) =>
    list.filter((e) => e.status === "valid" || e.status === "validating").length;

  const addTokens = (tokens: string[]) => {
    if (!tokens.length) return;
    const next = [...entries];
    const existingValues = new Set(
      next
        .filter((e) => e.value != null && e.status !== "invalid")
        .map((e) => e.value)
    );

    for (const token of tokens) {
      const parsed = parseProbillToken(token);
      if (!parsed.raw) continue;

      if (parsed.formatValid && parsed.value != null) {
        if (existingValues.has(parsed.value)) continue;
        if (countedTowardCap(next) >= maxValid) {
          if (props.maxValid != null) {
            const alreadyAtCap = next.some(
              (e) => e.status === "invalid" && e.raw === parsed.raw
            );
            if (!alreadyAtCap) {
              next.push({
                id: createEntryId(),
                raw: parsed.raw,
                value: parsed.value,
                status: "invalid",
                errorMessage: `Maximum of ${maxValid} ${capNoun}`,
              });
            }
          }
          continue;
        }
        existingValues.add(parsed.value);
        next.push({
          id: createEntryId(),
          raw: parsed.raw,
          value: parsed.value,
          status: "validating",
        });
      } else {
        const alreadyInvalid = next.some(
          (e) => e.status === "invalid" && e.raw === parsed.raw
        );
        if (alreadyInvalid) continue;
        next.push({
          id: createEntryId(),
          raw: parsed.raw,
          status: "invalid",
          errorMessage: invalidFormatMessage,
        });
      }
    }
    onEntriesChange(next);
  };

  const commitInput = () => {
    const parts = inputValue.split(/[\s,;\n]+/).filter(Boolean);
    if (!parts.length) return;
    addTokens(parts);
    onInputValueChange("");
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" || e.key === "," || e.key === " " || e.key === "Tab") {
      if (inputValue.trim()) {
        e.preventDefault();
        commitInput();
      }
    } else if (e.key === "Backspace" && !inputValue && entries.length > 0) {
      onEntriesChange(entries.slice(0, -1));
    }
  };

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    const text = e.clipboardData.getData("text");
    if (!text) return;
    e.preventDefault();
    const parts = text.split(/[\s,;\n]+/).filter(Boolean);
    addTokens(parts);
    onInputValueChange("");
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (/[\s,;]/.test(value)) {
      const parts = value.split(/[\s,;\n]+/).filter(Boolean);
      const endsWithSeparator = /[\s,;]$/.test(value);
      if (endsWithSeparator) {
        addTokens(parts);
        onInputValueChange("");
      } else {
        addTokens(parts.slice(0, -1));
        onInputValueChange(parts[parts.length - 1] || "");
      }
      return;
    }
    onInputValueChange(value);
  };

  const removeEntry = (id: string) => {
    onEntriesChange(entries.filter((e) => e.id !== id));
  };

  const clearAll = () => {
    onEntriesChange([]);
    onInputValueChange("");
  };

  return (
    <div className="xgs-print-download__chip-input">
      <div
        className="xgs-print-download__chip-field"
        onClick={() => inputRef.current?.focus()}
      >
        {entries.map((entry) => {
          if (entry.status === "valid" && entry.value != null) {
            return (
              <span key={entry.id} className="xgs-print-download__chip xgs-print-download__chip--valid">
                <XGSIcon icon={XGSIcons.faCheck} className="xgs-print-download__chip__icon" />
                <span>{formatProbillDisplay(entry.value)}</span>
                <button
                  type="button"
                  className="xgs-print-download__chip__remove"
                  aria-label={`Remove ${formatProbillDisplay(entry.value)}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    removeEntry(entry.id);
                  }}
                >
                  <XGSIcon icon={XGSIcons.faTimes} />
                </button>
              </span>
            );
          }

          if (entry.status === "validating" && entry.value != null) {
            return (
              <span key={entry.id} className="xgs-print-download__chip xgs-print-download__chip--validating">
                <span>{formatProbillDisplay(entry.value)}</span>
                <span className="xgs-print-download__chip__error">Verifying…</span>
                <button
                  type="button"
                  className="xgs-print-download__chip__remove"
                  aria-label={`Remove ${formatProbillDisplay(entry.value)}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    removeEntry(entry.id);
                  }}
                >
                  <XGSIcon icon={XGSIcons.faTimes} />
                </button>
              </span>
            );
          }

          return (
            <span key={entry.id} className="xgs-print-download__chip xgs-print-download__chip--invalid">
              <span>{entry.value != null ? formatProbillDisplay(entry.value) : entry.raw}</span>
              <span className="xgs-print-download__chip__error">
                {entry.errorMessage || invalidFormatMessage}
              </span>
              <button
                type="button"
                className="xgs-print-download__chip__remove"
                aria-label={`Remove ${entry.raw}`}
                onClick={(e) => {
                  e.stopPropagation();
                  removeEntry(entry.id);
                }}
              >
                <XGSIcon icon={XGSIcons.faTimes} />
              </button>
            </span>
          );
        })}
        <input
          ref={inputRef}
          className="xgs-print-download__chip-field__input"
          value={inputValue}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          onBlur={commitInput}
          placeholder={entries.length === 0 ? emptyPlaceholder : addPlaceholder}
        />
      </div>
      <div className="xgs-print-download__chip-footer">
        <div className="xgs-print-download__chip-footer__counts">
          {validCount > 0 && (
            <span className="xgs-print-download__chip-footer__valid">
              <XGSIcon icon={XGSIcons.faCheckCircle} /> {validCount} valid
            </span>
          )}
          {validatingCount > 0 && (
            <span className="xgs-print-download__chip-footer__validating">
              {validatingCount} verifying…
            </span>
          )}
          {invalidCount > 0 && (
            <span className="xgs-print-download__chip-footer__invalid">
              <XGSIcon icon={XGSIcons.faExclamationCircle} /> {invalidCount} invalid — will be excluded
            </span>
          )}
          {props.maxValid != null && validCount >= maxValid && (
            <span>Maximum of {maxValid} {capNoun}</span>
          )}
        </div>
        {entries.length > 0 && (
          <button type="button" className="xgs-print-download__link" onClick={clearAll}>
            Clear all
          </button>
        )}
      </div>
    </div>
  );
});

export default ProbillChipInput;
